/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { addYearsToList, getDateRange } from '@/account/lib/getDateRange';
import { useText } from '@adobe-commerce/elsie/i18n';
import { useCallback, useMemo, useState, useEffect } from 'preact/hooks';

export const useOrderSelectList = () => {
  const translations = useText({
    pastSixMonths:
      'Account.fullSizeView.OrdersList.OrdersListSelectDate.pastSixMonths',
    currentYear:
      'Account.fullSizeView.OrdersList.OrdersListSelectDate.currentYear',
    viewAll: 'Account.fullSizeView.OrdersList.OrdersListSelectDate.viewAll',
  });

  const [firstOrderDate, setFirstOrderDate] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<string>(
    JSON.stringify(getDateRange('sixMonthsAgo', 6))
  );
  const [selectedPage, setSelectedPage] = useState<number>(1);

  useEffect(() => {
    window?.scrollTo({
      top: 100,
      behavior: 'smooth',
    });
  }, [selectedPage]);

  const selectableDateList = useMemo(() => {
    const result = [
      {
        value: JSON.stringify(getDateRange('sixMonthsAgo', 6)),
        text: translations.pastSixMonths,
      },
      {
        value: JSON.stringify(getDateRange('oneYearAgo', 1)),
        text: translations.currentYear,
      },
      {
        value: 'viewAll',
        text: translations.viewAll,
      },
    ];

    if (firstOrderDate) {
      const date = new Date(firstOrderDate);
      const year = date.getFullYear();

      result?.splice(2, 0, ...addYearsToList(year));
    }

    return result;
  }, [translations, firstOrderDate]);

  const handleSetFirstOrderDate = useCallback((date: string) => {
    setFirstOrderDate(date);
  }, []);

  const handleSelectDate = useCallback((event: Event) => {
    const selectElement = event.target as HTMLSelectElement;

    const selectedValue = selectElement.value;

    setSelectedDate(selectedValue);
    setSelectedPage(1);
  }, []);

  return {
    selectableDateList,
    selectedDate,
    selectedPage,
    handleSelectDate,
    setSelectedPage,
    handleSetFirstOrderDate,
  };
};
